/**
 * Delivery / Recipient Information.
 *
 * Optional in full. The default — charity distribution to whoever needs it —
 * requires nothing at all, which is the case the charity is actually built
 * around. Naming a person, adding an instruction or paying for a doorstep
 * delivery are all opt-in on top of that.
 *
 * The address fields appear only once doorstep delivery is chosen, because for
 * charity distribution there is no address to give and asking for one would
 * imply the meals come to the donor.
 *
 * Nothing here decides the fee or the deliverable cities: both come from
 * `/config`, and the server re-checks them when the donation is created. This
 * is the form, not the rule.
 */

import { memo } from 'react';
import { StyleSheet, View } from 'react-native';

import { formatMoney, type DeliveryOption, type RecipientDetails } from '@mfa/domain';
import { colors, radius, space } from '../../theme';
import { Checkbox, RadioGroup } from '../forms/Choice';
import { Field } from '../forms/Field';
import { Text } from '../common/Text';
import { useT } from '../../core/i18n/LanguageProvider';

export interface RecipientSectionProps {
  value: RecipientDetails;
  onChange: (next: RecipientDetails) => void;
  delivery: DeliveryOption | undefined;
  requiresEvidence: boolean;
  onRequiresEvidenceChange: (next: boolean) => void;
  errors?: Record<string, string>;
  testID?: string;
}

export const RecipientSection = memo(function RecipientSection({
  value,
  onChange,
  delivery,
  requiresEvidence,
  onRequiresEvidenceChange,
  errors = {},
  testID = 'recipient-section',
}: RecipientSectionProps) {
  const t = useT();
  const doorstep = value.fulfilmentType === 'DOORSTEP_DELIVERY';
  const address = value.address ?? { addressLine: '', area: null, city: '', landmark: null };

  const set = (patch: Partial<RecipientDetails>) => onChange({ ...value, ...patch });
  const setAddress = (patch: Partial<typeof address>) =>
    set({ address: { ...address, ...patch } });

  // Only offered when an operator has switched it on and priced it. Showing a
  // disabled option would advertise something the charity cannot do.
  const options = [
    {
      value: 'CHARITY_DISTRIBUTION' as const,
      label: t('Charity Distribution'),
      description: t('We give the meals to people in need. Included.'),
    },
    ...(delivery?.enabled
      ? [
          {
            value: 'DOORSTEP_DELIVERY' as const,
            label: t(delivery.label),
            description: `${t(delivery.note)} · ${formatMoney(delivery.fee)} ${t('delivery fee')}`,
          },
        ]
      : []),
  ];

  return (
    <View style={styles.section} testID={testID}>
      {/* No heading: the screen's own title already says Recipient
          Information, and repeating it above the first field made the step
          look like it asked the same thing twice. */}
      {options.length > 1 ? (
        <RadioGroup
          value={value.fulfilmentType}
          onChange={(next) =>
            set({
              fulfilmentType: next,
              // Dropping back to charity distribution clears the address, so a
              // half-filled one cannot be submitted with the wrong mode.
              address: next === 'DOORSTEP_DELIVERY' ? value.address : null,
            })
          }
          options={options}
          testID={`${testID}-fulfilment`}
        />
      ) : null}

      <Field
        label={t('Recipient name')}
        icon="person"
        value={value.recipientName ?? ''}
        onChangeText={(text) => set({ recipientName: text || null })}
        placeholder={t('Optional — a specific person, if you have one in mind')}
        testID={`${testID}-recipient-name`}
      />

      <Field
        label={t('Instructions')}
        icon="info"
        value={value.instruction ?? ''}
        onChangeText={(text) => set({ instruction: text || null })}
        placeholder={t('e.g. Please deliver to any needy person you find.')}
        multiline
        testID={`${testID}-instruction`}
      />

      {doorstep ? (
        <View style={styles.doorstep} testID={`${testID}-address`}>
          <Text variant="cardTitle">{t('Address Details')}</Text>
          <Field
            label={t('Address')}
            required
            icon="location"
            value={address.addressLine}
            onChangeText={(text) => setAddress({ addressLine: text })}
            placeholder={t('House and street')}
            error={errors.addressLine}
            testID={`${testID}-address-line`}
          />
          <Field
            label={t('Area')}
            icon="location"
            value={address.area ?? ''}
            onChangeText={(text) => setAddress({ area: text || null })}
            placeholder={t('Optional')}
            testID={`${testID}-area`}
          />
          <Field
            label={t('City')}
            required
            icon="location"
            value={address.city}
            onChangeText={(text) => setAddress({ city: text })}
            placeholder={delivery?.cities.join(' or ') ?? 'City'}
            error={errors.city}
            testID={`${testID}-city`}
          />
          <Field
            label={t('Landmark')}
            icon="location"
            value={address.landmark ?? ''}
            onChangeText={(text) => setAddress({ landmark: text || null })}
            placeholder={t('Optional — helps the rider find you')}
            testID={`${testID}-landmark`}
          />

          <Text variant="cardTitle" style={styles.contactHeading}>{t('Contact Number')}</Text>
          <Field
            label={t("Recipient's number")}
            required
            icon="phone"
            value={value.recipientContact ?? ''}
            onChangeText={(text) => set({ recipientContact: text || null })}
            placeholder={t('Whoever will receive the meals')}
            keyboardType="phone-pad"
            error={errors.recipientContact}
            testID={`${testID}-recipient-contact`}
          />
        </View>
      ) : null}

      <View style={styles.evidence}>
        <Checkbox
          checked={requiresEvidence}
          onChange={onRequiresEvidenceChange}
          label={t('I need evidence of distribution')}
          testID={`${testID}-evidence`}
        />
        <Text variant="caption" tone="secondary" style={styles.evidenceHint}>{t('We will not close your donation until the distribution has been evidenced.')}</Text>
      </View>
    </View>
  );
});

const styles = StyleSheet.create({
  section: {
    gap: space.md,
    marginTop: space.xl,
  },
  hint: {
    marginTop: -space.xs,
  },
  doorstep: {
    gap: space.md,
    padding: space.md,
    borderRadius: radius.lg,
    backgroundColor: colors.bg.surfaceSunken,
  },
  contactHeading: {
    marginTop: space.xs,
  },
  evidence: {
    gap: space.xxs,
    marginTop: space.xs,
  },
  evidenceHint: {
    paddingLeft: space['2xl'],
  },
});
